import React, { useState, useMemo } from "react";
import { BarChart3, PackageSearch, Trophy } from "lucide-react";
import SearchInput from "../../CommonComponents/SearchInput";

const ProductSalesTable = ({ products }) => {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Filter and sort products
    const filteredProducts = useMemo(() => {
        let filtered = [...products];
        if (search.trim()) {
            const s = search.trim().toLowerCase();
            filtered = filtered.filter(
                p => (p.product?.name || p.product || p.product_id || "").toString().toLowerCase().includes(s)
            );
        }
        return filtered.sort((a, b) => parseInt(b.totalQty) - parseInt(a.totalQty));
    }, [products, search]);

    const totalPages = Math.max(Math.ceil(filteredProducts.length / pageSize), 1);
    const safePage = Math.min(page, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const paginated = filteredProducts.slice(startIndex, startIndex + pageSize);

    // Reset to first page if search or products change and current page is out of range
    React.useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [page, search, products, totalPages]);

    return (
        <section className="flex w-full flex-col gap-4 rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        <BarChart3 className="h-4 w-4" />
                        Product Movement
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">All Products Sold</h2>
                    <p className="mt-1 text-sm text-slate-500">Sorted from most to least sold across the selected branch scope.</p>
                </div>
                <div className="w-full lg:max-w-md">
                    <SearchInput value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search product..." />
                </div>
            </div>

            <div className="overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
                <table className="min-w-[720px] w-full border-collapse">
                    <thead
                        className="border-b"
                        style={{
                            background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                            borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                        }}
                    >
                        <tr>
                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Rank</th>
                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Product</th>
                            <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Quantity Sold</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-slate-400">
                                        <PackageSearch className="h-10 w-10" />
                                        <p className="mt-3 text-sm font-medium">No products found.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : paginated.map((p, idx) => (
                            <tr
                                key={p.product_id || p.product?.id || idx}
                                className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent"
                            >
                                <td className="px-4 py-3 text-left xl:px-5">
                                    <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-600 shadow-sm">
                                        {startIndex + idx + 1}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-left xl:px-5">
                                    <div className="flex items-center gap-3">
                                        <span
                                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
                                            style={{ background: "color-mix(in srgb, var(--color-primary) 86%, black)" }}
                                        >
                                            <Trophy className="h-4 w-4" />
                                        </span>
                                        <span className="font-semibold text-slate-900">{p.product?.name || p.product || p.product_id}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-center xl:px-5">
                                    <span
                                        className="inline-flex min-w-[92px] items-center justify-center rounded-2xl border px-3 py-1 text-xs font-semibold shadow-sm"
                                        style={{
                                            background: "color-mix(in srgb, var(--color-primary) 9%, white)",
                                            borderColor: "color-mix(in srgb, var(--color-primary) 18%, white)",
                                            color: "var(--color-primary)",
                                        }}
                                    >
                                        {Number(p.totalQty || 0).toLocaleString()} sold
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between gap-3 overflow-x-auto pb-1 text-xs whitespace-nowrap">
                <div className="shrink-0 text-gray-500">
                    Showing {filteredProducts.length === 0 ? 0 : startIndex + 1}
                    -{Math.min(startIndex + pageSize, filteredProducts.length)} of {filteredProducts.length} products
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                        onClick={() => setPage(Math.max(1, safePage - 1))}
                        disabled={safePage === 1}
                    >
                        Prev
                    </button>
                    <div className="text-xs text-gray-600">
                        Page {safePage} of {totalPages}
                    </div>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                        onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                        disabled={safePage === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>
        </section>
    );
};

export default ProductSalesTable;
